import type { PurchaseOrderStatus } from './purchase-order.js';

/**
 * Talep–temin karşılaştırması (ortağın notu #12; ADR 0047 §6). Talep satırı ↔ verilmiş PO satırı
 * (`requisition_line_id`) ↔ iptal edilmemiş GRN satırları. Saf fonksiyon: hangi siparişin
 * "verilmiş" sayılacağı (onaylanmış, iptal edilmemiş) ve hangi GRN'nin geçerli olduğu (iptal
 * edilmemiş) kayıt katmanında süzülür.
 */

export interface FulfilmentRequestLine {
  id: string;
  lineNo: number;
  materialId: string | null;
  name: string;
  unit: string;
  quantity: number;
  /** Talepteki tahmini birim fiyat (talebin para biriminde). */
  unitPrice: number | null;
  /**
   * Ofisin onayladığı miktar (ADR 0111 §A); kaldırılan satırda 0. Verilmezse istenen: revizyon
   * yoksa talep olduğu gibi onaylıdır.
   */
  approved?: number;
}

/** Ofisin revizyonla eklediği katalog kalemi (ADR 0111 §A): talep satırı değildir. */
export interface FulfilmentAddedLine {
  id: string;
  materialId: string;
  name: string;
  unit: string;
  approved: number;
}

export interface FulfilmentOrderLine {
  id: string;
  purchaseOrderId: string;
  poNo: string;
  orderStatus: PurchaseOrderStatus;
  requisitionLineId: string | null;
  materialId: string | null;
  name: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  currency: string;
  /**
   * İkame satırı (ADR 0111 §D5): başka üründür; istenen kalemi karşılamaz, ayrı satırda görünür
   * (ADR 0115). Eski sunucu ve "seç → PO" akışında yok.
   */
  substitute?: boolean;
  /** İkamenin yerine geçtiği istenen kalemin adı. */
  substituteFor?: string | null;
  /** Siparişin tedarikçisi ("Orders by Supplier"; ADR 0115). */
  supplierId?: string | null;
  supplierName?: string | null;
}

export interface FulfilmentReceiptLine {
  purchaseOrderLineId: string;
  accepted: number;
  damaged: number;
  missing: number;
}

export type FulfilmentStatus = 'not_ordered' | 'pending' | 'partial' | 'complete';

export interface FulfilmentRow {
  key: string;
  requisitionLineId: string | null;
  lineNo: number | null;
  materialId: string | null;
  name: string;
  unit: string;
  /** Geminin istediği; ofisin eklediği satırda 0. */
  requested: number;
  /** Ofisin onayladığı (ADR 0111 §A); revizyon yoksa istenen. Kalan buna göre. */
  approved: number;
  ordered: number;
  accepted: number;
  damaged: number;
  missing: number;
  /** Onaylanan − kabul; eksiye düşmez. */
  outstanding: number;
  status: FulfilmentStatus;
  /** Satırın geçtiği PO numaraları. */
  orders: string[];
  /** Son siparişin birim fiyatı ve para birimi; sipariş yoksa boş. */
  unitPrice: number | null;
  currency: string | null;
  /** Talepteki tahmini birim fiyat. */
  estimatedPrice: number | null;
  /** İkame satırı (ADR 0115): istenen kalemi karşılamaz; `substituteFor` istenenin adı. */
  substitute?: boolean;
  substituteFor?: string | null;
}

/** Tedarikçinin bu talepteki bir siparişi ("Orders by Supplier"; ADR 0115). */
export interface FulfilmentSupplierOrder {
  purchaseOrderId: string;
  poNo: string;
  status: PurchaseOrderStatus;
  currency: string;
  /** Kalemlerin toplamı (miktar × birim fiyat; başlık masrafları hariç). */
  total: number;
  lines: {
    id: string;
    name: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    accepted: number;
    substitute: boolean;
    substituteFor: string | null;
  }[];
}

export interface FulfilmentSupplierGroup {
  supplierId: string | null;
  supplierName: string;
  orders: FulfilmentSupplierOrder[];
}

export interface Fulfilment {
  rows: FulfilmentRow[];
  /** Siparişler tedarikçiye göre, tedarikçi adı ve PO numarası sırasıyla (ADR 0115). */
  bySupplier: FulfilmentSupplierGroup[];
  /** Verilmiş PO var mı. */
  hasOrders: boolean;
  /** Teslimi sürebilecek açık PO'lar (kalan hâlâ gelebilir). */
  openOrders: string[];
  hasShortfall: boolean;
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/** Teslimi hâlâ sürebilecek sipariş durumları. */
const OPEN: ReadonlySet<PurchaseOrderStatus> = new Set([
  'issued',
  'sent',
  'confirmed',
  'partially_delivered',
]);

function statusOf(row: { approved: number; ordered: number; accepted: number }) {
  const target = row.approved > 0 ? row.approved : row.ordered;
  if (row.ordered <= 0 && row.accepted <= 0) return 'not_ordered' as const;
  if (row.accepted >= target) return 'complete' as const;
  if (row.accepted <= 0) return 'pending' as const;
  return 'partial' as const;
}

export function requisitionFulfilment(input: {
  lines: readonly FulfilmentRequestLine[];
  /** Ofisin eklediği satırlar; PO satırı talep satırı bağı olmadan katalog kalemiyle eşleşir. */
  addedLines?: readonly FulfilmentAddedLine[];
  orderLines: readonly FulfilmentOrderLine[];
  receiptLines: readonly FulfilmentReceiptLine[];
}): Fulfilment {
  const received = new Map<string, { accepted: number; damaged: number; missing: number }>();
  for (const line of input.receiptLines) {
    const sum = received.get(line.purchaseOrderLineId) ?? { accepted: 0, damaged: 0, missing: 0 };
    sum.accepted += line.accepted;
    sum.damaged += line.damaged;
    sum.missing += line.missing;
    received.set(line.purchaseOrderLineId, sum);
  }
  const build = (
    base: Omit<
      FulfilmentRow,
      | 'ordered'
      | 'accepted'
      | 'damaged'
      | 'missing'
      | 'outstanding'
      | 'status'
      | 'orders'
      | 'unitPrice'
      | 'currency'
    >,
    orders: readonly FulfilmentOrderLine[],
  ): FulfilmentRow => {
    let ordered = 0;
    let accepted = 0;
    let damaged = 0;
    let missing = 0;
    for (const line of orders) {
      ordered += line.quantity;
      const sum = received.get(line.id);
      accepted += sum?.accepted ?? 0;
      damaged += sum?.damaged ?? 0;
      missing += sum?.missing ?? 0;
    }
    const values = {
      approved: base.approved,
      ordered: round(ordered),
      accepted: round(accepted),
    };
    const last = orders.at(-1);
    return {
      ...base,
      ordered: values.ordered,
      accepted: values.accepted,
      damaged: round(damaged),
      missing: round(missing),
      outstanding: Math.max(0, round(base.approved - accepted)),
      status: statusOf(values),
      orders: [...new Set(orders.map((line) => line.poNo))],
      unitPrice: last?.unitPrice ?? null,
      currency: last?.currency ?? null,
    };
  };
  const rows = [...input.lines]
    .sort((a, b) => a.lineNo - b.lineNo)
    .map((line) =>
      build(
        {
          key: line.id,
          requisitionLineId: line.id,
          lineNo: line.lineNo,
          materialId: line.materialId,
          name: line.name,
          unit: line.unit,
          requested: line.quantity,
          approved: line.approved ?? line.quantity,
          estimatedPrice: line.unitPrice,
        },
        // İkame istenen kalemi karşılamaz (ADR 0115; ADR 0111 §D11)
        input.orderLines.filter(
          (order) => order.requisitionLineId === line.id && order.substitute !== true,
        ),
      ),
    );
  const lineNoOf = new Map(input.lines.map((line) => [line.id, line.lineNo]));
  const known = new Set(input.lines.map((line) => line.id));
  // Ofisin eklediği satır: talep satırı bağı olmayan PO satırı aynı katalog kalemiyle eşleşir
  const added = new Map((input.addedLines ?? []).map((line) => [line.materialId, line]));
  const matched = new Set<string>();
  for (const line of input.addedLines ?? []) {
    const orders = input.orderLines.filter(
      (order) => order.requisitionLineId === null && order.materialId === line.materialId,
    );
    for (const order of orders) matched.add(order.id);
    rows.push(
      build(
        {
          key: line.id,
          requisitionLineId: null,
          lineNo: null,
          materialId: line.materialId,
          name: line.name,
          unit: line.unit,
          requested: 0,
          approved: line.approved,
          estimatedPrice: null,
        },
        orders,
      ),
    );
  }
  for (const order of input.orderLines) {
    if (order.substitute === true) {
      // İkame kendi satırında: istenen ve onaylanan yok, kalan yok; istenenin satır numarasıyla
      rows.push(
        build(
          {
            key: order.id,
            requisitionLineId: null,
            lineNo:
              order.requisitionLineId === null
                ? null
                : (lineNoOf.get(order.requisitionLineId) ?? null),
            materialId: order.materialId,
            name: order.name,
            unit: order.unit,
            requested: 0,
            approved: 0,
            estimatedPrice: null,
            substitute: true,
            substituteFor: order.substituteFor ?? null,
          },
          [order],
        ),
      );
      continue;
    }
    if (order.requisitionLineId !== null && known.has(order.requisitionLineId)) continue;
    if (matched.has(order.id) || (order.materialId !== null && added.has(order.materialId))) {
      continue;
    }
    rows.push(
      build(
        {
          key: order.id,
          requisitionLineId: null,
          lineNo: null,
          materialId: order.materialId,
          name: order.name,
          unit: order.unit,
          requested: 0,
          approved: 0,
          estimatedPrice: null,
        },
        [order],
      ),
    );
  }
  return {
    rows,
    bySupplier: groupBySupplier(input.orderLines, received),
    hasOrders: input.orderLines.length > 0,
    openOrders: [
      ...new Set(
        input.orderLines.filter((line) => OPEN.has(line.orderStatus)).map((line) => line.poNo),
      ),
    ].sort(),
    hasShortfall: rows.some((row) => row.outstanding > 0),
  };
}

/** Siparişleri tedarikçiye göre gruplar (ADR 0115). */
function groupBySupplier(
  lines: readonly FulfilmentOrderLine[],
  received: ReadonlyMap<string, { accepted: number }>,
): FulfilmentSupplierGroup[] {
  const groups = new Map<string, FulfilmentSupplierGroup>();
  const orders = new Map<string, FulfilmentSupplierOrder>();
  for (const line of lines) {
    const name = line.supplierName ?? '';
    const key = line.supplierId ?? `name:${name}`;
    let group = groups.get(key);
    if (!group) {
      group = { supplierId: line.supplierId ?? null, supplierName: name, orders: [] };
      groups.set(key, group);
    }
    let order = orders.get(line.purchaseOrderId);
    if (!order) {
      order = {
        purchaseOrderId: line.purchaseOrderId,
        poNo: line.poNo,
        status: line.orderStatus,
        currency: line.currency,
        total: 0,
        lines: [],
      };
      orders.set(line.purchaseOrderId, order);
      group.orders.push(order);
    }
    order.total = round(order.total + line.quantity * line.unitPrice);
    order.lines.push({
      id: line.id,
      name: line.name,
      unit: line.unit,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      accepted: round(received.get(line.id)?.accepted ?? 0),
      substitute: line.substitute === true,
      substituteFor: line.substitute === true ? (line.substituteFor ?? null) : null,
    });
  }
  const sorted = [...groups.values()].sort(
    (a, b) =>
      a.supplierName.localeCompare(b.supplierName) ||
      (a.supplierId ?? '').localeCompare(b.supplierId ?? ''),
  );
  for (const group of sorted) group.orders.sort((a, b) => a.poNo.localeCompare(b.poNo));
  return sorted;
}

export interface ShortfallLine {
  materialId: string | null;
  name: string;
  unit: string;
  quantity: number;
  unitPrice: number | null;
}

/**
 * Eksik için yeni talebin satırları (ADR 0047 §6): kalan miktarlı talep satırları. Tahmini fiyat
 * siparişten, yalnız sipariş talebin para birimindeyse; farklıysa boş (sistemde kur yok, fiyat
 * dönüştürülmeden taşınmaz). Sipariş yoksa talepteki tahmin (talebin para biriminde). Kullanıcı
 * taslakta düzeltir. Gemi sipariş fiyatını görmez (sahip kararı 2026-11-05; ADR 0169): `orderPrices:
 * false` ile fiyat her zaman talepteki tahmindir; sunucu fiyatı göndermese de aynı.
 */
export function shortfallLines(
  fulfilment: Fulfilment,
  currency: string,
  options: { orderPrices?: boolean } = {},
): ShortfallLine[] {
  const orderPrices = options.orderPrices !== false;
  return fulfilment.rows
    .filter((row) => row.requisitionLineId !== null && row.outstanding > 0)
    .map((row) => ({
      materialId: row.materialId,
      name: row.name,
      unit: row.unit,
      quantity: row.outstanding,
      unitPrice:
        !orderPrices || row.unitPrice === null || row.unitPrice === undefined
          ? row.estimatedPrice
          : row.currency === currency
            ? row.unitPrice
            : null,
    }));
}
